import { env } from "@/lib/env"
import { isClientSide } from "@/lib/is-client-side"
import { mtLocation } from "@/lib/mt-location"
import { isEuRegion } from "@/lib/region"

export function isEmbedded() {
  return (
    isClientSide() &&
    (mtLocation.pathname.startsWith("/embedded") || isMicrositeHost())
  )
}

export function embeddedOrgid() {
  if (!isClientSide()) return undefined
  const fromMicrosite = micrositeOrgid()
  if (fromMicrosite) return fromMicrosite
  if (mtLocation.pathname.startsWith("/embedded")) {
    const match = mtLocation.pathname.match(/^\/embedded\/([\w-]+)/)
    return match ? match[1] : null
  }
}

// host page URL, set by embed.js via the iframe hash.
export function embeddedSourceUrl(): string | undefined {
  return embeddedFrameProp("orgUrl")
}

// partner's own visitor id (News Corp's NKID), set by embed.js via the iframe hash.
export function embeddedSourceId(): string | undefined {
  return embeddedFrameProp("nkid")
}

function embeddedFrameProp(name: "orgUrl" | "nkid"): string | undefined {
  if (!isClientSide() || !isEmbedded()) return undefined
  try {
    const hash = mtLocation.hash || ""
    const decoded = decodeURIComponent(hash.split("#")[1] ?? "")
    if (!decoded) return undefined
    const props = JSON.parse(decoded) as Partial<
      Record<"orgUrl" | "nkid", string>
    >
    return props[name]
  } catch {
    return undefined
  }
}

// orgids that are served as full-page microsites at <orgid>.mindtrip.ai.
const MICROSITE_ORGIDS: ReadonlySet<string> = new Set(
  (env("NEXT_PUBLIC_MICROSITE_ORGIDS") ?? "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean)
)

export function isMicrositeOrgid(orgid: string): boolean {
  return MICROSITE_ORGIDS.has(orgid)
}

// Pass a host string for server-side checks (e.g. context.req.headers.host).
// Call with no args on the client to read from window.location.
export function isMicrositeHost(host?: string): boolean {
  if (host !== undefined) {
    return MICROSITE_ORGIDS.has(subdomain(host))
  }
  if (!isClientSide()) return false
  // matches <orgid>.mindtrip.ai, <orgid>.mindtrip-eu.ai, <orgid>.mindtrip-local.ai, etc.
  return MICROSITE_ORGIDS.has(subdomain(mtLocation.host))
}

export function micrositeOrgid(): string | null {
  if (!isMicrositeHost()) return null
  return subdomain(mtLocation.host)
}

function subdomain(host: string): string {
  const dot = host.indexOf(".")
  return dot === -1 ? host : host.slice(0, dot)
}

export function getPartnerHost(): string {
  return isEuRegion()
    ? "https://partner.mindtrip-eu.ai"
    : "https://partner.mindtrip.ai"
}
